import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  Method,
  AxiosProgressEvent,
} from "axios";
import Config from "../Config";

const getAuthToken = (): string | null => {
  return localStorage.getItem("access_token");
};

type ProgressCallback = (progressEvent: AxiosProgressEvent) => void;

interface ApiRequestOptions {
  method: Method;
  path: string;
  data?: any;
  token?: boolean;
  contentType?: string;
  progressCallback?: ProgressCallback | null;
}

const apiRequest = async ({
  method,
  path,
  data = {},
  token = true,
  contentType = "application/json",
  progressCallback = null,
}: ApiRequestOptions): Promise<AxiosResponse<any>> => {
  const headers: Record<string, string> = {
    // Don't set Content-Type for multipart/form-data — axios must set it
    // automatically so it includes the required boundary parameter.
    ...(contentType !== "multipart/form-data" ? { "Content-Type": contentType } : {}),
    ...(token ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
  };

  const scheme = Config.DISABLE_SSL ? "http" : "https";

  const config: AxiosRequestConfig = {
    method,
    url: `${scheme}://${Config.HOSTNAME}/api${path}`,
    headers,
    data,
    onUploadProgress: progressCallback ?? ((_: AxiosProgressEvent) => {}), // fallback empty function
  };

  try {
    const response = await axios(config);

    if (response.status >= 200 && response.status < 300) {
      return response;
    } else {
      const error = new Error(
        `Request failed with status ${response.status}`,
      ) as any;
      error.response = response;
      throw error;
    }
  } catch (error) {
    throw error;
  }
};

const get = (path: string, token = true, contentType = "application/json") =>
  apiRequest({ method: "GET", path, token, contentType });

const post = (
  path: string,
  data: any,
  token = true,
  contentType = "application/json",
  progressCallback: ProgressCallback | null = null,
) =>
  apiRequest({
    method: "POST",
    path,
    data,
    token,
    contentType,
    progressCallback,
  });

const del = (path: string, token = true, contentType = "application/json") =>
  apiRequest({ method: "DELETE", path, token, contentType });

const patch = (
  path: string,
  data: any,
  token = true,
  contentType = "application/json",
) => apiRequest({ method: "PATCH", path, data, token, contentType });

// health check endpoint
export const checkHealth = async () => {
  try {
    // use a simple endpoint that doesn't require authentication
    await get("/auth/keycloak/vars", false);
    return { healthy: true, api: true };
  } catch (error) {
    return { healthy: false, api: false };
  }
};

// auth endpoints
export const exchangeKeycloakCode = (code: string) =>
  post(`/auth/keycloak/callback?code=${code}`, {}, false);
export const getCurrentUser = () => get("/auth/me");

// document endpoints
export const addDocument = (name: string, organisationId?: string) => {
  const params = new URLSearchParams({ name });
  if (organisationId) {
    params.append("organisation_id", organisationId);
  }
  return post(`/document/?${params.toString()}`, {});
};
export const addPage = (
  documentID: string,
  file: File,
  progressCallback: ProgressCallback | null = null,
  insertMode?: "start" | "after" | "end",
  insertAfterPage?: number,
) => {
  const form = new FormData();
  form.append("file", file);
  const params = new URLSearchParams();
  if (insertMode) {
    params.append("insert_mode", insertMode);
  }
  if (insertAfterPage !== undefined && insertMode === "after") {
    params.append("insert_after_page", insertAfterPage.toString());
  }
  const queryString = params.toString();
  const url = `/document/${documentID}/pages/${queryString ? `?${queryString}` : ""}`;
  return post(url, form, true, "multipart/form-data", progressCallback);
};

export const getDocument = (documentID: string) =>
  get(`/document/${documentID}/`);
export const getDocumentPages = (documentID: string) =>
  get(`/document/${documentID}/pages/`);
export const getPageData = (documentID: string, pageID: string) =>
  get(`/document/${documentID}/pages/${pageID}/?data=true`);
export const getPageDataWithBbox = (documentID: string, pageID: string) =>
  get(`/document/${documentID}/pages/${pageID}/?data=true&include_bbox=true`);
export const getPageImage = (documentID: string, pageID: string) =>
  get(`/document/${documentID}/pages/${pageID}/?image=true`);

export const deleteDocument = (documentID: string) =>
  del(`/document/${documentID}/`);
export const deletePage = (documentID: string, pageID: string) =>
  del(`/document/${documentID}/pages/${pageID}/`);
export const renameDocument = (extractionId: string, newName: string) =>
  patch(`/document/${extractionId}/?name=${newName}`, {});

export const getJobList = (
  params: {
    limit?: number;
    offset?: number;
    search?: string;
    tag_id?: string;
    untagged?: boolean;
    sort_order?: string;
  } = {},
) => {
  const query = new URLSearchParams();
  query.append("thumbnail", "true");
  if (params.limit !== undefined) query.append("limit", String(params.limit));
  if (params.offset !== undefined)
    query.append("offset", String(params.offset));
  if (params.search) query.append("search", params.search);
  if (params.tag_id) query.append("tag_id", params.tag_id);
  if (params.untagged) query.append("untagged", "true");
  if (params.sort_order) query.append("sort_order", params.sort_order);
  return get(`/document/?${query.toString()}`);
};

export const getMoleculeSVG = (
  transcription: string,
  depictParams: Record<string, string> = {},
  token = false,
) => {
  const defaultParams = {
    abbr: "off",
    hdisp: "S",
    zoom: "1.3",
    annotate: "none",
    r: "0",
    ...depictParams,
  };
  const allParams = new URLSearchParams({
    smi: transcription,
    ...defaultParams,
  });
  return get(`/depict?${allParams.toString()}`, token, "application/json");
};

/** Render a molecule from its OCSR graph (atoms + bonds) → PNG blob */
export const getMoleculeRender = (graph: {
  symbols: string[];
  coords: number[][];
  edges: number[][];
  bbox_width?: number;
  bbox_height?: number;
}) => {
  const scheme = Config.DISABLE_SSL ? "http" : "https";
  return axios.post(
    `${scheme}://${Config.HOSTNAME}/api/document/molecule/render`,
    graph,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getAuthToken()}`,
      },
      responseType: "blob",
    },
  );
};

// label endpoints
export const createTag = (name: string, color: string = "#3B82F6") =>
  post("/document/tags/", { name, color });
export const getTags = () => get("/document/tags/");
export const getTag = (tagId: string) => get(`/document/tags/${tagId}/`);
export const updateTag = (tagId: string, name?: string, color?: string) =>
  patch(`/document/tags/${tagId}/`, { name, color });
export const deleteTag = (tagId: string) => del(`/document/tags/${tagId}/`);
export const addTagToDocument = (tagId: string, documentId: string) =>
  post(`/document/tags/${tagId}/documents/${documentId}/`, {});
export const removeTagFromDocument = (tagId: string, documentId: string) =>
  del(`/document/tags/${tagId}/documents/${documentId}/`);

// organization endpoints
export const getUserOrganizations = () =>
  get("/organization/user/organizations");
export const getAllOrganizations = () => get("/organization/");
export const getOrganization = (organizationId: string) =>
  get(`/organization/${organizationId}/`);
export const getOrganizationMembers = (organizationId: string) =>
  get(`/organization/${organizationId}/members`);
export const addOrganizationMember = (
  organizationId: string,
  email: string,
  role: string = "member",
) => post(`/organization/${organizationId}/members`, { email, role });
export const updateOrganizationMemberRole = (
  organizationId: string,
  memberId: string,
  role: string,
) => patch(`/organization/${organizationId}/members/${memberId}`, { role });
export const removeOrganizationMember = (
  organizationId: string,
  memberId: string,
) => del(`/organization/${organizationId}/members/${memberId}`);
export const resendMemberInvitation = (
  organizationId: string,
  memberId: string,
) =>
  post(
    `/organization/${organizationId}/members/${memberId}/resend-invitation`,
    {},
  );
export const searchUsers = (query: string) =>
  get(`/organization/search-users?query=${encodeURIComponent(query)}`);
export const createOrganization = (name: string, adminEmail: string) =>
  post("/organization/", { name, admin_email: adminEmail });
export const updateOrganizationSettings = (
  organizationId: string,
  settings: {
    allow_mobile_upload?: boolean;
    allow_member_download?: boolean;
    hide_member_emails?: boolean;
    name?: string;
    contact_phones?: string[];
    contact_emails?: string[];
  },
) => patch(`/organization/${organizationId}/settings`, settings);
export const getOrganizationDocuments = (
  organizationId: string,
  params: {
    limit?: number;
    offset?: number;
    search?: string;
    sort_order?: string;
  } = {},
) => {
  const query = new URLSearchParams();
  query.append("thumbnail", "true");
  if (params.limit !== undefined) query.append("limit", String(params.limit));
  if (params.offset !== undefined)
    query.append("offset", String(params.offset));
  if (params.search) query.append("search", params.search);
  if (params.sort_order) query.append("sort_order", params.sort_order);
  return get(`/document/organisation/${organizationId}?${query.toString()}`);
};

// document sharing endpoints
export const shareDocument = (documentId: string, userIds: string[]) =>
  post(`/document/${documentId}/shares`, { user_ids: userIds });

export const getDocumentShares = (documentId: string) =>
  get(`/document/${documentId}/shares`);

export const removeDocumentShare = (documentId: string, shareId: string) =>
  del(`/document/${documentId}/shares/${shareId}`);

// bulk upload / admin download
export const getBulkUploadFolders = () => get("/document/bulk-upload/folders");

export const downloadBulkUserFiles = (userEmail: string) => {
  const scheme = Config.DISABLE_SSL ? "http" : "https";
  return axios.get(
    `${scheme}://${Config.HOSTNAME}/api/document/bulk-upload/download?user_email=${encodeURIComponent(userEmail)}`,
    {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
      responseType: "blob",
    },
  );
};

export const bulkUpload = (
  entries: { file: File; name: string }[],
  progressCallback: ProgressCallback | null = null,
) => {
  const form = new FormData();
  for (const { file, name } of entries) {
    form.append("files", file);
    form.append("names", name);
  }
  return post("/document/bulk-upload", form, true, "multipart/form-data", progressCallback);
};

// admin endpoints
export const getAdminUserStats = (range: string = "1M") =>
  get(`/admin/stats/users?range=${range}`);
export const getAdminOrgStats = (range: string = "1M") =>
  get(`/admin/stats/organizations?range=${range}`);
export const getAdminDocumentStats = () => get("/admin/stats/documents");
export const getAdminAccountStats = () => get("/admin/stats/accounts");
export const getAdminActiveUsersStats = () => get("/admin/stats/active-users");
